/**
 * Project creation / creation-request mutation hooks (ZEUS-125/132/133).
 *
 * Dual-mode, matching the established pattern elsewhere in this app
 * (useMutations.ts's useRaiseSpend/useCreateReport): a pure function over
 * the seed (mutations.ts) is applied directly in demo mode; in real-API
 * mode the backend call is the source of truth and nothing is mirrored
 * locally (TanStack Query invalidation triggers the refetch instead).
 *
 * `Project.id`/`Programme.id` are typed `string` everywhere in this app,
 * but the concrete value differs by mode: real-API mode uses stringified
 * integers (matching the actual DB row), demo mode uses slugs like
 * "fs-04-entebbe". Callers (CreateProjectModal, WorkspacePages,
 * ProjectManagementView) pass the raw id through unchanged — converting to
 * a real numeric id for the backend call is this file's job, not theirs.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api, demoModeActive } from "./api";
import type { CreateZeusProjectResult, SubmitProjectCreationRequestResult } from "./api";
import { useMutate, useActorId, useActiveProfile, useSeed } from "./hooks";
import {
  createZeusProject as createZeusProjectDemo,
  editZeusProject as editZeusProjectDemo,
  changeZeusProjectStatus as changeZeusProjectStatusDemo,
  submitProjectCreationRequest as submitProjectCreationRequestDemo,
  rejectProjectCreationRequest as rejectProjectCreationRequestDemo,
  addMilestone as addMilestoneDemo,
} from "./mutations";

/**
 * Converts a raw id (string or number) to a real integer for the backend.
 * Returns `undefined` for a demo-mode slug rather than `NaN` — callers in
 * real-API mode always have a numeric-string id, so this only ever discards
 * something in demo mode, where it's never sent to the backend anyway.
 */
export function toNumericId(id: string | number | null | undefined): number | undefined {
  if (id == null) return undefined;
  const n = Number(id);
  return Number.isFinite(n) ? n : undefined;
}

function extractError(e: unknown): string {
  if (e instanceof AxiosError) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.message || data?.error || e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

export interface CreateZeusProjectHookInput {
  name: string;
  description?: string;
  justification: string;
  status?: "draft" | "review" | "published";
  /** Raw id (workspace/programme) — this hook converts for the real API. */
  programmeId?: string;
  /** Raw id (a project) — this hook converts for the real API; in demo mode,
   * matched directly against the seed's projects. */
  parentProjectId?: string;
  /** Raw id, ZEUS-129's creation-request link-on-accept — accepting a
   * pending request marks it accepted + links the created project, in both
   * demo and real-API mode (ZEUS-138). */
  requestId?: string;
}

export function useCreateZeusProject() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();

  async function createZeusProject(
    input: CreateZeusProjectHookInput,
  ): Promise<CreateZeusProjectResult | null> {
    if (demoModeActive()) {
      let newId = "";
      mutate((s) => {
        const out = createZeusProjectDemo(s, {
          name: input.name,
          description: input.description,
          status: input.status,
          parentProjectId: input.parentProjectId,
          requestId: input.requestId,
          actorId,
          actorOrganisationId: profile.organisationId,
        });
        newId = out.id;
        return out.seed;
      });
      return { project: { id: newId, name: input.name, title: input.name, status: input.status ?? "draft" } };
    }

    setIsPending(true);
    setError(null);
    try {
      const result = await api.createZeusProject({
        name: input.name,
        description: input.description,
        justification: input.justification,
        status: input.status,
        programmeId: toNumericId(input.programmeId),
        parentProjectId: toNumericId(input.parentProjectId),
        requestId: toNumericId(input.requestId),
      });
      if (input.programmeId != null) {
        // Prefix key (not ["programme-data", input.programmeId]) — matches
        // useEditZeusProject/useChangeZeusProjectStatus's own ZEUS-141-gotcha
        // comment: an exact-key invalidation silently no-ops if it doesn't
        // match the cached variant verbatim.
        queryClient.invalidateQueries({ queryKey: ["programme-data"] });
      }
      if (input.parentProjectId != null) {
        // Adding a sub-project — its own list lives under a separate key
        // that programme-data's invalidation above doesn't cover at all.
        queryClient.invalidateQueries({ queryKey: ["sub-projects", input.parentProjectId] });
      }
      queryClient.invalidateQueries({ queryKey: ["cross-deal-projects"] });
      if (input.requestId != null) {
        // Accepting a request (creating the project with requestId set) moves
        // it out of the pending queue — refresh ZEUS-138's review tab.
        queryClient.invalidateQueries({ queryKey: ["project-creation-requests"] });
      }
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { createZeusProject, isPending, error };
}

export interface EditZeusProjectHookInput {
  name?: string;
  description?: string;
  region?: string;
  district?: string;
  budgeted?: number;
  justification: string;
}

export function useEditZeusProject() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();

  /** `projectId` is the raw id returned by createZeusProject/whatever the
   * caller already has — a demo id (from the branch above) or a real one. */
  async function editZeusProject(
    projectId: string | number,
    input: EditZeusProjectHookInput,
  ): Promise<CreateZeusProjectResult | null> {
    if (demoModeActive()) {
      mutate((s) =>
        editZeusProjectDemo(
          s,
          String(projectId),
          {
            name: input.name,
            description: input.description,
            region: input.region,
            district: input.district,
            budgeted: input.budgeted,
          },
          { actorId, actorOrganisationId: profile.organisationId },
        ),
      );
      return { project: { id: projectId, name: input.name ?? "", title: input.name ?? "", status: "draft" } };
    }

    const numericId = toNumericId(projectId);
    if (numericId === undefined) return null; // demo-created id in real mode shouldn't happen; nothing to patch

    setIsPending(true);
    setError(null);
    try {
      const result = await api.editZeusProject(numericId, input);
      // ZEUS-143 — this hook previously had no invalidation at all (only
      // ever called internally by CreateProjectModal's own follow-up
      // patch, where a fresh project mount picked up the change anyway).
      // Now that EditProjectModal is a standalone, re-openable surface on
      // an already-mounted page, invalidate so the edit is visible without
      // a manual refresh. Prefix key only (ZEUS-141 gotcha) — an exact
      // ["programme-data", <id>] key can silently no-op if it doesn't
      // match the cached variant verbatim.
      queryClient.invalidateQueries({ queryKey: ["programme-data"] });
      queryClient.invalidateQueries({ queryKey: ["project-governance-history", String(projectId)] });
      queryClient.invalidateQueries({ queryKey: ["cross-deal-projects"] });
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { editZeusProject, isPending, error };
}

export interface CreateMilestoneHookInput {
  milestonenumber: string;
  description: string;
  targetDate?: string;
  plannedBudget?: number;
}

/** Adds a milestone to a project — used by CreateProjectModal (right after
 * the project itself is created) and EditProjectModal's Milestones section.
 * `projectId` is the raw id (demo slug or real numeric-string) the same way
 * every other hook here takes it; project-management's milestone routes
 * accept id/uuid/slug interchangeably server-side, so unlike editZeusProject
 * this needs no toNumericId conversion. */
export function useCreateMilestone() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutate = useMutate();
  const actorId = useActorId();

  async function createMilestone(
    projectId: string | number,
    input: CreateMilestoneHookInput,
  ): Promise<boolean> {
    const id = String(projectId);
    if (demoModeActive()) {
      mutate((s) => addMilestoneDemo(s, id, input, actorId));
      return true;
    }
    setIsPending(true);
    setError(null);
    try {
      await api.createMilestone(id, input);
      queryClient.invalidateQueries({ queryKey: ["milestones", id] });
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { createMilestone, isPending, error };
}

export interface ChangeZeusProjectStatusHookInput {
  status: "draft" | "review" | "published";
  justification: string;
}

/**
 * ZEUS-142 — direct status toggle among draft/review/published, no
 * adjacency restriction (matches the backend, which allows any-to-any).
 */
export function useChangeZeusProjectStatus() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();

  async function changeZeusProjectStatus(
    projectId: string | number,
    input: ChangeZeusProjectStatusHookInput,
  ): Promise<CreateZeusProjectResult | null> {
    if (demoModeActive()) {
      mutate((s) =>
        changeZeusProjectStatusDemo(s, String(projectId), input.status, {
          actorId,
          actorOrganisationId: profile.organisationId,
        }),
      );
      return { project: { id: projectId, name: "", title: "", status: input.status } };
    }

    const numericId = toNumericId(projectId);
    if (numericId === undefined) return null;

    setIsPending(true);
    setError(null);
    try {
      const result = await api.changeZeusProjectStatus(numericId, input);
      // ZEUS-141 gotcha: invalidate the prefix key (no second element) so
      // this matches the cached programme-data query regardless of the
      // exact programmeId — an exact-key invalidation silently no-ops if
      // it doesn't match verbatim.
      queryClient.invalidateQueries({ queryKey: ["programme-data"] });
      queryClient.invalidateQueries({ queryKey: ["project-governance-history", String(projectId)] });
      queryClient.invalidateQueries({ queryKey: ["cross-deal-projects"] });
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { changeZeusProjectStatus, isPending, error };
}

export interface SubmitProjectCreationRequestHookInput {
  proposedName: string;
  proposedDescription: string;
  justification: string;
  programmeId?: string;
  parentProjectId?: string;
}

export function useSubmitProjectCreationRequest() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();

  async function submitProjectCreationRequest(
    input: SubmitProjectCreationRequestHookInput,
  ): Promise<SubmitProjectCreationRequestResult | null> {
    if (demoModeActive()) {
      // ZEUS-138: persists a re-fetchable pending row in the seed store
      // (superseding ZEUS-132/133's fake-audit-entry-only simulation), so
      // the stratus review queue has something real to show in demo mode.
      let newId = "";
      mutate((s) => {
        const out = submitProjectCreationRequestDemo(s, {
          proposedName: input.proposedName,
          proposedDescription: input.proposedDescription,
          justification: input.justification,
          parentProjectId: input.parentProjectId,
          actorId,
          actorOrganisationId: profile.organisationId,
        });
        newId = out.id;
        return out.seed;
      });
      return { request: { id: newId, status: "pending" } };
    }

    setIsPending(true);
    setError(null);
    try {
      return await api.submitProjectCreationRequest({
        proposedName: input.proposedName,
        proposedDescription: input.proposedDescription,
        justification: input.justification,
        programmeId: toNumericId(input.programmeId),
        parentProjectId: toNumericId(input.parentProjectId),
      });
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { submitProjectCreationRequest, isPending, error };
}

/**
 * ZEUS-138 — a project-creation request normalized for display, regardless
 * of whether it came from the demo seed store or the real API (their raw
 * shapes differ: camelCase demo fields vs. lowercase DB columns).
 */
export interface ProjectCreationRequestRow {
  id: string | number;
  proposedName: string;
  proposedDescription: string;
  requesterRole: string;
  justification: string;
  parentProjectId?: string;
  programmeId?: string;
  createdAt: string;
}

/**
 * ZEUS-138 — the stratus_admin review queue. Demo mode reads (and reacts
 * live to) the seed's projectCreationRequests; real-API mode queries the
 * backend. Both branches normalize to ProjectCreationRequestRow so callers
 * don't need to know which mode they're in.
 */
export function useProjectCreationRequests(
  status: "pending" | "accepted" | "rejected" = "pending",
) {
  const demo = demoModeActive();
  const seed = useSeed();
  const query = useQuery({
    queryKey: ["project-creation-requests", status],
    queryFn: () => api.getProjectCreationRequests(status),
    enabled: !demo,
    staleTime: 30_000,
  });

  if (demo) {
    const data: ProjectCreationRequestRow[] = (seed.projectCreationRequests ?? [])
      .filter((r) => r.status === status)
      .map((r) => ({
        id: r.id,
        proposedName: r.proposedName,
        proposedDescription: r.proposedDescription,
        requesterRole: r.requesterRole,
        justification: r.justification,
        parentProjectId: r.parentProjectId,
        createdAt: r.createdAt,
      }));
    return { data, isLoading: false, isDemo: true as const };
  }

  const data: ProjectCreationRequestRow[] = (query.data ?? []).map((r) => ({
    id: r.id,
    proposedName: r.proposedname,
    proposedDescription: r.proposeddescription,
    requesterRole: r.requesterrole,
    justification: r.justification,
    parentProjectId: r.parentprojectid != null ? String(r.parentprojectid) : undefined,
    programmeId: r.programmeid != null ? String(r.programmeid) : undefined,
    createdAt: r.createdat,
  }));
  return { ...query, data, isDemo: false as const };
}

export function useRejectProjectCreationRequest() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutate = useMutate();
  const actorId = useActorId();
  const profile = useActiveProfile();

  async function rejectProjectCreationRequest(
    id: string | number,
    reason: string,
  ): Promise<boolean> {
    if (demoModeActive()) {
      mutate((s) =>
        rejectProjectCreationRequestDemo(s, String(id), reason, {
          actorId,
          actorOrganisationId: profile.organisationId,
        }),
      );
      return true;
    }

    setIsPending(true);
    setError(null);
    try {
      await api.rejectProjectCreationRequest(id, reason);
      queryClient.invalidateQueries({ queryKey: ["project-creation-requests"] });
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    } finally {
      setIsPending(false);
    }
  }

  return { rejectProjectCreationRequest, isPending, error };
}
